/**
 * Canonical renewal semantics shared by the browser and Edge Functions.
 *
 * A sales close date is deliberately not accepted as input. Renewal only
 * applies to explicitly marked retainers, using either their recorded end date
 * or a deterministic start-date + duration calculation.
 */

const DAY_MS = 24 * 60 * 60 * 1000;
const DATE_ONLY_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

export const RENEWAL_DUE_SOON_DAYS = 60;
/**
 * Meeting text falls back to Europe/London for a profile with no zone
 * (user-time-zone.ts), and Nynch's users are overwhelmingly in the UK. Renewal
 * dates use the same fallback so a renewal and a meeting agree about "today".
 */
export const DEFAULT_RENEWAL_TIME_ZONE = 'Europe/London';

export type RenewalState =
  | 'not_applicable'
  | 'missing'
  | 'scheduled'
  | 'due_soon'
  | 'overdue';

export type RenewalDateSource =
  | 'retainer_end_date'
  | 'retainer_start_and_duration';

export interface RenewalSemanticsInput {
  isRetainer: boolean | null | undefined;
  retainerEndDate?: string | null;
  retainerStartDate?: string | null;
  durationMonths?: number | null;
}

/**
 * Renewal dates are commercial calendar dates, so every caller must name the
 * user's saved IANA timezone. Browser locale and process timezone are never
 * consulted. Missing or invalid saved preferences resolve deterministically to
 * Europe/London, the same fallback meeting text uses, before the comparison is
 * made. A zone that is named (including 'UTC') is always honoured.
 */
export interface RenewalCalendarContext {
  timeZone: string;
  now?: Date;
}

export interface RenewalSemantics {
  applicable: boolean;
  state: RenewalState;
  endDate: string | null;
  source: RenewalDateSource | null;
  daysUntil: number | null;
  weeksUntil: number | null;
}

function parseDateMillis(value: string | null | undefined): number | null {
  if (!value) return null;
  const normalized = DATE_ONLY_PATTERN.test(value)
    ? `${value}T00:00:00.000Z`
    : value;
  const millis = Date.parse(normalized);
  return Number.isFinite(millis) ? millis : null;
}

export function normalizeRenewalTimeZone(timeZone: string | null | undefined): string {
  const candidate = timeZone?.trim() || DEFAULT_RENEWAL_TIME_ZONE;
  try {
    new Intl.DateTimeFormat('en', { timeZone: candidate }).format(0);
    return candidate;
  } catch {
    return DEFAULT_RENEWAL_TIME_ZONE;
  }
}

function calendarDateMillis(now: Date, timeZone: string): number {
  const parts = new Intl.DateTimeFormat('en', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(now);
  const year = Number(parts.find((part) => part.type === 'year')?.value);
  const month = Number(parts.find((part) => part.type === 'month')?.value);
  const day = Number(parts.find((part) => part.type === 'day')?.value);
  return Date.UTC(year, month - 1, day);
}

function getDaysUntil(
  endDate: string,
  endMillis: number,
  now: Date,
  timeZone: string,
): number {
  if (DATE_ONLY_PATTERN.test(endDate)) {
    return Math.round((endMillis - calendarDateMillis(now, timeZone)) / DAY_MS);
  }
  return Math.ceil((endMillis - now.getTime()) / DAY_MS);
}

function addCalendarMonths(
  startDate: string | null | undefined,
  durationMonths: number | null | undefined,
): string | null {
  const startMillis = parseDateMillis(startDate);
  const duration = Number(durationMonths);
  if (startMillis === null || !Number.isFinite(duration) || duration <= 0) {
    return null;
  }

  const months = Math.trunc(duration);
  if (months <= 0) return null;

  const result = new Date(startMillis);
  const originalDay = result.getUTCDate();
  result.setUTCDate(1);
  result.setUTCMonth(result.getUTCMonth() + months);
  const lastDayOfTargetMonth = new Date(Date.UTC(
    result.getUTCFullYear(),
    result.getUTCMonth() + 1,
    0,
  )).getUTCDate();
  result.setUTCDate(Math.min(originalDay, lastDayOfTargetMonth));

  return startDate && DATE_ONLY_PATTERN.test(startDate)
    ? result.toISOString().slice(0, 10)
    : result.toISOString();
}

export function resolveRenewalSemantics(
  input: RenewalSemanticsInput,
  calendar: RenewalCalendarContext,
): RenewalSemantics {
  const now = calendar.now ?? new Date();
  const timeZone = normalizeRenewalTimeZone(calendar.timeZone);
  if (input.isRetainer !== true) {
    return {
      applicable: false,
      state: 'not_applicable',
      endDate: null,
      source: null,
      daysUntil: null,
      weeksUntil: null,
    };
  }

  const explicitEndMillis = parseDateMillis(input.retainerEndDate);
  const derivedEndDate = explicitEndMillis === null
    ? addCalendarMonths(input.retainerStartDate, input.durationMonths)
    : null;
  const endDate = explicitEndMillis !== null ? input.retainerEndDate ?? null : derivedEndDate;
  const endMillis = explicitEndMillis ?? parseDateMillis(derivedEndDate);
  const source: RenewalDateSource | null = explicitEndMillis !== null
    ? 'retainer_end_date'
    : endMillis !== null
      ? 'retainer_start_and_duration'
      : null;

  if (!endDate || endMillis === null || source === null) {
    return {
      applicable: true,
      state: 'missing',
      endDate: null,
      source: null,
      daysUntil: null,
      weeksUntil: null,
    };
  }

  const daysUntil = getDaysUntil(endDate, endMillis, now, timeZone);
  const state: RenewalState = daysUntil < 0
    ? 'overdue'
    : daysUntil <= RENEWAL_DUE_SOON_DAYS
      ? 'due_soon'
      : 'scheduled';

  return {
    applicable: true,
    state,
    endDate,
    source,
    daysUntil,
    weeksUntil: Math.floor(daysUntil / 7),
  };
}
